import { useState, useEffect, useCallback } from 'react';
import { STORAGE_KEYS } from '../types';

export type OnboardingStatus = 'not-started' | 'guided' | 'explored' | 'completed';

export type OnboardingHostingChoice = 'managed' | 'self';

/** Sub-phase of the categories step */
export type CategoriesPhase = 'questions' | 'review';

export interface OnboardingJourneyState {
  status: OnboardingStatus;
  /** Current step: 1 account, 2 password, 3 categories, 4 bank accounts, 5 statements, 6 debts, 7 goals, 8 done */
  step: number;
  /** Schema version for migrating persisted journey state */
  journeyVersion?: number;
  /** User confirmed they have an Actual Budget instance deployed */
  hasInstance: boolean;
  /** How the user chose to host Actual Budget (onboarding is managed-only from v5) */
  hostingChoice: OnboardingHostingChoice | null;
  /** Categories were created in the connected budget during onboarding */
  categoriesCreated: boolean;
  /** Questionnaire vs review editor within the categories step */
  categoriesPhase?: CategoriesPhase;
  /** Budget currency chosen during onboarding (defaults from main spending currency) */
  budgetCurrency?: string;
}

const JOURNEY_VERSION = 10;

const DEFAULT_STATE: OnboardingJourneyState = {
  status: 'not-started',
  step: 1,
  hasInstance: false,
  hostingChoice: 'managed',
  categoriesCreated: false,
  categoriesPhase: 'questions',
  budgetCurrency: 'USD',
  journeyVersion: JOURNEY_VERSION
};

function migrateJourneyState(state: OnboardingJourneyState): OnboardingJourneyState {
  const fromVersion = state.journeyVersion ?? 1;
  const migrated: OnboardingJourneyState = {
    ...DEFAULT_STATE,
    ...state,
    journeyVersion: JOURNEY_VERSION,
    categoriesPhase: state.categoriesPhase ?? 'questions',
    budgetCurrency: state.budgetCurrency || 'USD'
  };

  // v1 order: 1 instance, 2 account → v2+ order: 1 account, 2 instance
  if (fromVersion < 2) {
    if (migrated.status === 'guided' && migrated.step === 1) {
      migrated.step = 2;
    } else if (migrated.status === 'guided' && migrated.step === 2) {
      migrated.step = 1;
    }
  }

  // v4: 1 account, 2 instance, 3 connect, 4 categories, 5 done
  // v5: 1 account, 2 activate, 3 categories, 4 done
  if (fromVersion < 5 && migrated.status === 'guided') {
    if (migrated.step === 2 || migrated.step === 3) {
      migrated.step = 2;
    } else if (migrated.step === 4) {
      migrated.step = 3;
    } else if (migrated.step === 5) {
      migrated.step = 4;
    }
    migrated.hostingChoice = 'managed';
  }

  // v6: 1 account, 2 categories, 3 credentials, 4 done
  // v7: 1 account, 2 categories, 3 bank accounts, 4 credentials, 5 statements, 6 done
  if (fromVersion < 6 && fromVersion >= 5 && migrated.status === 'guided') {
    if (migrated.step === 2) {
      migrated.step = 2;
    } else if (migrated.step === 3) {
      migrated.step = 2;
    } else if (migrated.step === 4) {
      migrated.step = 4;
    }
  }

  if (fromVersion < 7 && fromVersion >= 6 && migrated.status === 'guided') {
    if (migrated.step === 3) {
      migrated.step = 4;
    } else if (migrated.step === 4) {
      migrated.step = 6;
    }
  }

  // v7: 1 account, 2 categories, 3 bank accounts, 4 credentials, 5 statements, 6 done
  // v8: 1 account, 2 password, 3 categories, 4 bank accounts, 5 statements, 6 done
  if (fromVersion < 8 && fromVersion >= 7 && migrated.status === 'guided') {
    if (migrated.step === 2) {
      migrated.step = 3;
    } else if (migrated.step === 3) {
      migrated.step = 4;
    } else if (migrated.step === 4) {
      migrated.step = 2;
    }
  }

  // v8: 1 account, 2 password, 3 categories, 4 bank accounts, 5 balances, 6 done
  // v9: adds step 6 debts, done moves to 7
  if (fromVersion < 9 && fromVersion >= 8 && migrated.status === 'guided' && migrated.step === 6) {
    migrated.step = 7;
  }

  // v9: done at step 7
  // v10: adds step 7 goals, done moves to 8
  if (fromVersion < 10 && fromVersion >= 9 && migrated.status === 'guided' && migrated.step === 7) {
    migrated.step = 8;
  }

  if (migrated.status === 'guided') {
    migrated.hostingChoice = 'managed';
  }

  return migrated;
}

function loadInitialState(): OnboardingJourneyState {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.ONBOARDING_JOURNEY);
    if (stored) {
      return migrateJourneyState(JSON.parse(stored));
    }
    if (localStorage.getItem(STORAGE_KEYS.ONBOARDING_DISMISSED)) {
      return { ...DEFAULT_STATE, status: 'explored' };
    }
  } catch (error) {
    console.error('Failed to load onboarding journey state:', error);
  }
  return DEFAULT_STATE;
}

export const useOnboardingJourney = () => {
  const [journey, setJourney] = useState<OnboardingJourneyState>(loadInitialState);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.ONBOARDING_JOURNEY, JSON.stringify(journey));
    } catch (error) {
      console.error('Failed to persist onboarding journey state:', error);
    }
  }, [journey]);

  const updateJourney = useCallback((changes: Partial<OnboardingJourneyState>) => {
    setJourney(prev => ({ ...prev, ...changes }));
  }, []);

  return { journey, updateJourney };
};
